import { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Calculator as CalcIcon, ArrowRight } from "lucide-react";

const fmt = (n) => "$" + Math.round(n).toLocaleString();

export default function Calculator() {
  const [revenue, setRevenue] = useState([45000]); // monthly revenue
  const [term, setTerm] = useState([9]); // months
  const [factor, setFactor] = useState([1.28]); // factor rate (10x scale display)

  const monthlyRev = revenue[0];
  const months = term[0];
  const factorRate = factor[0];

  // Estimated funding: ~ 1.0x – 1.4x monthly revenue (we display 1.2x midpoint)
  const estimatedFunding = useMemo(() => {
    const baseMultiplier = 1.0 + (months / 12) * 0.4; // longer term = slightly more
    return monthlyRev * baseMultiplier;
  }, [monthlyRev, months]);

  const totalRepay = estimatedFunding * factorRate;
  const weeklyPayment = totalRepay / (months * 4.33);

  return (
    <section id="calculator" data-testid="calculator-section" className="py-24 md:py-32 bg-[#070F1B] border-y border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="text-xs tracking-[0.25em] uppercase font-bold text-gold mb-5">Funding Calculator</div>
          <h2 className="font-heading text-4xl md:text-5xl font-light text-white tracking-tighter">
            See your funding range in <span className="text-gold">seconds</span>.
          </h2>
          <p className="mt-6 text-white/60 text-lg leading-relaxed">
            Estimate how much capital you can qualify for and what repayment looks like.
            Final terms depend on your full application — but most clients land within 5%
            of these figures.
          </p>
          <div className="mt-8 inline-flex items-center gap-3 text-sm text-white/50">
            <CalcIcon className="w-4 h-4 text-gold" />
            Estimates only · Not a commitment to fund
          </div>
        </div>

        <div className="lg:col-span-7 bg-[#0A1326] border border-white/10 p-8 md:p-12">
          <div className="space-y-10">
            <div>
              <div className="flex items-center justify-between mb-4">
                <label className="text-sm tracking-wider uppercase text-white/70">Monthly Revenue</label>
                <span className="font-heading text-2xl text-gold" data-testid="calc-revenue">{fmt(monthlyRev)}</span>
              </div>
              <Slider
                value={revenue}
                onValueChange={setRevenue}
                min={10000}
                max={500000}
                step={1000}
                data-testid="calc-revenue-slider"
              />
              <div className="flex justify-between text-xs text-white/40 mt-2">
                <span>$10K</span><span>$500K</span>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-4">
                <label className="text-sm tracking-wider uppercase text-white/70">Term Length</label>
                <span className="font-heading text-2xl text-gold" data-testid="calc-term">{months} months</span>
              </div>
              <Slider value={term} onValueChange={setTerm} min={3} max={24} step={1} data-testid="calc-term-slider" />
              <div className="flex justify-between text-xs text-white/40 mt-2">
                <span>3 mo</span><span>24 mo</span>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-4">
                <label className="text-sm tracking-wider uppercase text-white/70">Factor Rate</label>
                <span className="font-heading text-2xl text-gold" data-testid="calc-factor">{factorRate.toFixed(2)}x</span>
              </div>
              <Slider value={factor} onValueChange={setFactor} min={1.15} max={1.45} step={0.01} data-testid="calc-factor-slider" />
              <div className="flex justify-between text-xs text-white/40 mt-2">
                <span>1.15x (best)</span><span>1.45x</span>
              </div>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-px bg-white/10 border border-white/10">
            <div className="bg-[#050B14] p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Estimated Funding</div>
              <div className="font-heading text-2xl md:text-3xl text-gold" data-testid="calc-funding">{fmt(estimatedFunding)}</div>
            </div>
            <div className="bg-[#050B14] p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Weekly Payment</div>
              <div className="font-heading text-2xl md:text-3xl text-white" data-testid="calc-weekly">{fmt(weeklyPayment)}</div>
            </div>
            <div className="bg-[#050B14] p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Total Repayment</div>
              <div className="font-heading text-2xl md:text-3xl text-white" data-testid="calc-total">{fmt(totalRepay)}</div>
            </div>
          </div>

          <a
            href="#apply"
            data-testid="calc-cta"
            className="mt-8 group w-full inline-flex items-center justify-center gap-3 bg-gold hover:bg-gold-400 text-navy-950 font-semibold px-8 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_28px_rgba(212,175,55,0.4)]"
          >
            Lock in this rate <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
          </a>
        </div>
      </div>
    </section>
  );
}
